/* eslint-disable react/require-default-props */
import React, { useContext, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useImpression, ImpressionProvider } from '@thd-olt-component-react/impression';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withErrorBoundary } from '@thd-olt-component-react/error-boundary';
import {
  useDataModel,
  params,
  string,
  extend,
  shape
} from '@thd-nucleus/data-sources';
import { initSurvey } from '../qualtricsHelper';
import { SearchFeedbackPlaceholder } from './SearchFeedbackPlaceholder/SearchFeedbackPlaceholder';
import './search-feedback.scss';

const SearchFeedbackComponent = ({
  type = 'search',
  impressionID = null,
  hasCategoryPageCondition = false
}) => {
  const { channel, customContext } = useContext(ExperienceContext);
  const { variables } = customContext || {};
  const { keyword = '', navParam } = variables || {};

  const { ref, clickID } = useImpression({
    data: {
      id: impressionID || 'SearchFeedbackID',
      name: 'SearchFeedback',
      component: 'SearchFeedback',
      position: 1,
      type: 'content'
    }
  });

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search-feedback.ready');
    initSurvey();
  }, []);

  const defaultOptions = {
    variables,
    ssr: true,
    skip: !keyword && !navParam,
  };

  const { data, loading, error } = useDataModel('searchModel', defaultOptions);
  const { searchModel = {} } = data || {};
  const { metadata = {} } = searchModel || {};
  const isCategory = metadata?.contentType === 'categorypage';

  if (channel === 'mobile') return null;

  if (!data || loading || error) {
    return null;
  }
  if (hasCategoryPageCondition || isCategory) {
    return null;
  }

  const rowClasses = classNames({
    collectionFeedbackWrapper: type === 'collection'
  });

  const columnClasses = classNames({
    'article-feedback-wrapper': type === 'articles',
    'search-feedback-wrapper': type !== 'articles'
  });

  return (
    <div
      className="sui-grid"
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
    >
      <div
        className={rowClasses}
        data-component="SearchFeedback"

      >
        <div className={columnClasses}>
          <div id={`${type}Feedback`} data-testid={`${type}Feedback`} />
        </div>
      </div>
    </div>
  );
};

SearchFeedbackComponent.displayName = 'SearchFeedbackComponent';

SearchFeedbackComponent.propTypes = {
  type: PropTypes.oneOf([
    'articles',
    'search',
    'collection'
  ]),
  impressionID: PropTypes.string,
  hasCategoryPageCondition: PropTypes.bool
};

SearchFeedbackComponent.dataModel = {
  searchModel: params({ keyword: string(), navParam: string() }).shape({
    metadata: shape({
      contentType: string()
    })
  })
};

const SearchFeedback = ({
  type = 'search',
  impressionID = null,
  data = null,
  hasCategoryPageCondition = false,
  ...restProps
}) => {
  const fallbackData = {
    id: impressionID || 'SearchFeedbackID',
    component: 'SearchFeedback',
    name: 'SearchFeedback',
    type: 'content'
  };

  const impressionData = data || fallbackData;

  return (
    <ImpressionProvider
      key="SearchFeedback"
      data={impressionData}
    >
      <SearchFeedbackComponent
        type={type}
        impressionID={impressionID}
        hasCategoryPageCondition={hasCategoryPageCondition}
      />
    </ImpressionProvider>
  );
};

const placeholder = (
  <SearchFeedbackPlaceholder />
);

SearchFeedback.displayName = 'SearchFeedback';

SearchFeedback.propTypes = {
  type: PropTypes.oneOf([
    'articles',
    'search',
    'collection'
  ]),
  impressionID: PropTypes.string,
  hasCategoryPageCondition: PropTypes.bool,
  data: PropTypes.shape({
    id: PropTypes.string,
    component: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string
  })
};

SearchFeedback.dataModel = extend(SearchFeedbackComponent);

const HydratedSearchFeedback = withHydrator({
  id: 'feedback',
  scrollBuffer: 750,
  delay: 2000,
  preserveCtxVal: 'clientStore',
  placeholder,
}, SearchFeedback);

const DynamicSearchFeedback = withDynamicComponent(HydratedSearchFeedback, { placeholder });
const SearchFeedbackWithBoundary = withErrorBoundary(DynamicSearchFeedback);

SearchFeedbackWithBoundary.dataModel = SearchFeedback.dataModel;
SearchFeedbackWithBoundary.propTypes = SearchFeedback.propTypes;
SearchFeedbackWithBoundary.displayName = SearchFeedback.displayName;

export { SearchFeedbackWithBoundary as SearchFeedback };
